// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The spinner (M3-25, 07 §3.4).
//
// "A pulse train whose rate follows the spinner's angular velocity — so it accelerates as the
// ball passes and ratchets down as it slows, which is exactly what the ear expects and what
// makes a good spinner shot feel enormous."
//
// **The rate is the whole sound, and getting it right is a timing problem rather than a
// synthesis one.** The click itself is fifteen milliseconds of plastic and switch blade; what
// makes a rip exciting is that forty of them arrive in a second and then thin out over the next
// two. Every one of those clicks already exists as an `EV_SPINNER_REVOLUTION`, stamped with the
// exact simulation step the vane passed top dead centre — so the sound does not have to predict
// anything, model anything, or track a phase. It has to *play them when they happened*.
//
// **Which is precisely what a frame loop cannot do.** Sixteen simulation steps arrive together
// and are handed to the audio layer at one instant. A spinner at forty revolutions a second is a
// click every twenty-five milliseconds; quantised to a sixty-hertz frame it becomes a click
// every seventeen or every thirty-three, alternating. That is a third of the interval, and it is
// the difference between a ratchet and a stumble. Nothing else in the machine cares — a bumper
// is a single event and a frame of slop is inaudible on it — but a *rhythm* made of frames
// sounds like a machine with a fault.
//
// So spinner clicks are **scheduled**, not played: each is started at a Web Audio time derived
// from its own step index, which restores the exact intervals the simulation produced. The price
// is a fixed delay, because the events describe steps that have already run — see
// `SPINNER_LATENCY`. Nothing else in the product pays it, and the spinner is the one voice for
// which it is free: it is a continuous rattle rather than a response to a button.

import {
  addNoiseBurst,
  addPartial,
  Biquad,
  fadeOut,
  normalise,
  samplesFor,
  type NoiseSource,
  type Samples,
} from './dsp'
import { STEP_DT } from '@core/sim/constants'

/**
 * How far ahead of the events a scheduled click is placed, seconds.
 *
 * An event describes a step that has already run, so its natural time is in the past and there
 * is nothing to do but delay the whole train. The floor is the longest burst of steps one frame
 * can carry: `MAX_STEPS_PER_FRAME` is 40, so a frame that has fallen behind hands over events up
 * to forty milliseconds old, and a shorter latency would collapse the oldest of them onto *now*
 * — reintroducing the bunching this exists to remove, exactly when the machine is struggling.
 *
 * Fifty milliseconds is imperceptible on a rattle. It would not be on a flipper, which is why
 * nothing else is scheduled this way.
 */
export const SPINNER_LATENCY = 0.05

/** A click: the vane's edge passing its stop, and the switch blade behind it. */
const CLICK_NOISE_HZ = 2800
const CLICK_NOISE_Q = 1.5
const CLICK_NOISE_MS = 2.5
/** The blade: a short bright ring, which is what makes it a *switch* and not a tap. */
const BLADE_HZ = 4200
const BLADE_DECAY_MS = 6
const BLADE_GAIN = 0.45
/** The vane's own body, low and quiet. Without it the click has no object behind it. */
const BODY_HZ = 400
const BODY_DECAY_MS = 11
const BODY_GAIN = 0.3

export function spinnerClickLengthMs(): number {
  return Math.max(CLICK_NOISE_MS, BLADE_DECAY_MS, BODY_DECAY_MS) * 1.15 + 8
}

/**
 * Render one click.
 *
 * The shortest cue in the bank after the ball pair, and it has to be: at forty a second, two
 * consecutive clicks are twenty-five milliseconds apart, and a click that outlasts its own
 * interval turns a ratchet into a buzz.
 */
export function renderSpinnerClick(
  sampleRate: number,
  noise: NoiseSource,
  pitchScale = 1,
): Samples {
  const out = new Float32Array(samplesFor(spinnerClickLengthMs(), sampleRate))

  addPartial(out, sampleRate, BLADE_HZ * pitchScale, BLADE_GAIN, BLADE_DECAY_MS)
  addPartial(out, sampleRate, BODY_HZ * pitchScale, BODY_GAIN, BODY_DECAY_MS)
  addNoiseBurst(
    out,
    sampleRate,
    noise,
    Biquad.bandpass(CLICK_NOISE_HZ * pitchScale, CLICK_NOISE_Q, sampleRate),
    1,
    CLICK_NOISE_MS,
  )

  normalise(out)
  fadeOut(out, sampleRate)
  return out
}

/**
 * Angular velocity at which a click is at full level, rad/s.
 *
 * A well-hit spinner passes forty revolutions a second, which is about 250 rad/s; this is a
 * shade under that, so a genuinely good shot spends a moment at the top of the range rather
 * than only touching it.
 */
export const SPINNER_REFERENCE_OMEGA = 220

/** Below this a revolution is the vane wobbling over the top, and it is nearly silent. */
export const SPINNER_MIN_OMEGA = 2

/**
 * Click level from angular velocity, 0…1.
 *
 * The same soft knee the impacts use, and for the same reason: linear, the last dozen clicks of
 * a coast — which are the ones a player is listening to, because they are still scoring — fall
 * away to nothing well before the vane stops. The knee keeps the tail audible while leaving the
 * rip clearly the loudest part.
 */
export function spinnerLevel(omega: number): number {
  const speed = omega < 0 ? -omega : omega
  if (speed < SPINNER_MIN_OMEGA) return 0
  const raw = Math.min(speed / SPINNER_REFERENCE_OMEGA, 1)
  return Math.pow(raw, 0.55)
}

/**
 * How far ahead of `now` a click may be placed before the clock is judged to have drifted.
 *
 * Three latencies. Below that the audio clock and the simulation are keeping company and the
 * epoch is left alone; above it the simulation has outrun the wall clock — slow motion, a burst
 * of catch-up steps — and holding the old epoch would queue clicks further and further into the
 * future until the spinner was audibly behind the vane on screen.
 */
export const MAX_CLICK_LEAD = SPINNER_LATENCY * 3

/**
 * The map from simulation steps to context time.
 *
 * **One anchor, held across frames**, and that is the entire point. Deriving each click's time
 * from *this* frame's `now` and `stepIndex` is exact within the frame and wrong between frames:
 * the audio clock runs on wall time and the simulation runs on however many steps the driver
 * managed, so any divergence between them lands on the frame boundary as a stretched or
 * squashed interval. Measured in the browser, a rip scheduled that way produced gaps of
 * 18, 25, 19, 22, 43, 18 ms where the physics says 27, 29, 30, 32, 34, 37 — jitter of about
 * sixty per cent, which is worse than the frame quantisation the scheduling exists to remove.
 *
 * With a fixed epoch every click is `epoch + step × 1 ms`, so the intervals are the simulation's
 * own however the frames fell. The epoch is re-taken only when it has stopped being usable —
 * when a click would land in the past, which Web Audio silently treats as *now*, or so far ahead
 * that the sound has come adrift from the picture.
 */
export class ClickClock {
  /** Context time at which step 0 would have sounded. */
  private epoch = 0
  private anchored = false

  /** Times the epoch had to be re-taken. A number that climbs during play is a stalling driver. */
  reanchors = 0

  /**
   * Context time for a click at `step`.
   *
   * `now` is the context's current time, read once per frame by the caller.
   */
  timeFor(step: number, now: number): number {
    if (!this.anchored) return this.anchor(step, now)
    const at = this.epoch + step * STEP_DT
    if (at < now || at > now + MAX_CLICK_LEAD) {
      this.reanchors++
      return this.anchor(step, now)
    }
    return at
  }

  /**
   * Forget the anchor, as when the machine is rebuilt.
   *
   * Step indices belong to a simulation that no longer exists; an epoch computed against them
   * would put the new machine's first rip somewhere between the last century and the next one.
   */
  reset(): void {
    this.anchored = false
    this.reanchors = 0
  }

  private anchor(step: number, now: number): number {
    this.epoch = now + SPINNER_LATENCY - step * STEP_DT
    this.anchored = true
    return this.epoch + step * STEP_DT
  }
}
